import { FC } from "react";

import { AssignedPagesList } from "components/materials/AssignedPagesList";
import { HideUnlessEditModeButton } from "components/materials/HideUnlessEditModeButton";
import { ContentSidebarHeader } from "components/materials/sidebar/ContentSidebarHeader";
import { ContentSidebarMain } from "components/materials/sidebar/ContentSidebarMain";
import { PagesCard } from "components/materials/topic/activity/detail/PagesCard";
import { SectionPageCategory } from "components/server-types";
import { t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { HorizontalDivider } from "mds/components/HorizontalDivider";
import { useOrdered } from "mds/hooks/use-ordered";
import { EyeNotVisibleIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectAllPagesWithDueDates,
  selectIsEditModeEnabled,
  selectPagesForCurrentSection,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { SCROLL_TRACKING_IDS } from "utils/hooks/use-scroll-restoration";
import { usePageActions } from "utils/page";

interface SectionPageSidebarPanelProps {
  pageCategory: SectionPageCategory;
}

export const SectionPageSidebarPanel: FC<SectionPageSidebarPanelProps> = ({ pageCategory }) => {
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const { canAuthorSection } = useCapabilities();
  const pagesWithDueDates = useListSelector(selectAllPagesWithDueDates);
  const pages = useOrdered(useListSelector((s) => selectPagesForCurrentSection(s, pageCategory)));

  const { createPage } = usePageActions();
  const isAssignmentView = pageCategory === "assignment";

  const createNewPage = () => {
    createPage(pageCategory);
  };

  const noPages = isAssignmentView
    ? pagesWithDueDates.length + pages.length === 0
    : pages.length === 0;

  return (
    <>
      <ContentSidebarHeader visibleOnMobile>
        <div className="flex w-full items-center">
          <h3 className="flex flex-auto items-center gap-2 text-black-tint-20">
            {t(`page_variant_plural.${pageCategory}`)}

            {pageCategory === "instructor_workspace" && <EyeNotVisibleIcon />}
          </h3>
        </div>

        <HorizontalDivider className="my-3 -ml-3" />
      </ContentSidebarHeader>

      <ContentSidebarMain scrollTrackingId={SCROLL_TRACKING_IDS.PAGE_SIDEBAR}>
        {isAssignmentView ? (
          <>
            {pages.length > 0 && (
              <PagesCard
                canManagePages={canAuthorSection}
                category={pageCategory}
                header={t("page_sidebar.section_assignments")}
                pages={pages}
              />
            )}
            {pages.length === 0 && !noPages && isEditModeEnabled && (
              <>
                <div className="body-s mt-1 w-full truncate px-2 text-black-tint-40">
                  {t("page_sidebar.section_assignments")}
                </div>
                <Card className="mt-2" small>
                  <div className="-ml-1">
                    <HideUnlessEditModeButton reference="page" onClick={createNewPage} />
                  </div>
                </Card>
              </>
            )}

            {(pages.length > 0 || (!noPages && isEditModeEnabled)) && (
              <HorizontalDivider className="mb-3 mt-5" hideIfLast />
            )}
          </>
        ) : (
          <PagesCard canManagePages={canAuthorSection} category={pageCategory} pages={pages} />
        )}

        {noPages && (
          <Card className="mt-3">
            <div className="body-s px-2 py-1 font-light text-black-tint-40">
              {t("page_sidebar.no_available", { context: pageCategory })}
            </div>

            <div className="-ml-1">
              <HideUnlessEditModeButton reference="page" onClick={createNewPage} />
            </div>
          </Card>
        )}

        {isAssignmentView && <AssignedPagesList category="topic" timeframe="all" />}
      </ContentSidebarMain>
    </>
  );
};
